import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Loader2, Globe, Minimize2, Maximize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Supported languages
const LANGUAGES = [
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Português', flag: '🇧🇷' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'fa', name: 'فارسی', flag: '🇮🇷' },
];

// Greeting messages in different languages
const GREETINGS = {
  tr: 'Merhaba! 👋 Size nasıl yardımcı olabilirim?',
  en: 'Hello! 👋 How can I help you today?',
  de: 'Hallo! 👋 Wie kann ich Ihnen helfen?',
  fr: 'Bonjour! 👋 Comment puis-je vous aider?',
  es: '¡Hola! 👋 ¿Cómo puedo ayudarte?',
  ar: 'مرحباً! 👋 كيف يمكنني مساعدتك؟',
  it: 'Ciao! 👋 Come posso aiutarti?',
  pt: 'Olá! 👋 Como posso ajudá-lo?',
  ru: 'Привет! 👋 Как я могу вам помочь?',
  zh: '您好！👋 我能帮您什么？',
  fa: 'سلام! 👋 چطور می‌توانم کمکتان کنم؟',
};

// Placeholder text
const PLACEHOLDERS = {
  tr: 'Mesajınızı yazın...',
  en: 'Type your message...',
  de: 'Nachricht eingeben...',
  fr: 'Écrivez votre message...',
  es: 'Escribe tu mensaje...',
  ar: 'اكتب رسالتك...',
  it: 'Scrivi il tuo messaggio...',
  pt: 'Digite sua mensagem...',
  ru: 'Введите сообщение...',
  zh: '输入您的消息...',
  fa: 'پیام خود را بنویسید...',
};

// Quick action buttons labels
const QUICK_ACTIONS = {
  tr: {
    trackOrder: '📦 Sipariş Takip',
    shipping: '🚚 Kargo Bilgisi',
    returns: '↩️ İade',
    aboutCompany: '🏢 Firma Hakkında',
    products: '🛍️ Ürünler',
    contact: '📞 İletişim',
  },
  en: {
    trackOrder: '📦 Track Order',
    shipping: '🚚 Shipping',
    returns: '↩️ Returns',
    aboutCompany: '🏢 About Us',
    products: '🛍️ Products',
    contact: '📞 Contact',
  },
  de: {
    trackOrder: '📦 Bestellung verfolgen',
    shipping: '🚚 Versand',
    returns: '↩️ Rückgabe',
    aboutCompany: '🏢 Über uns',
    products: '🛍️ Produkte',
    contact: '📞 Kontakt',
  },
  fr: {
    trackOrder: '📦 Suivre commande',
    shipping: '🚚 Livraison',
    returns: '↩️ Retours',
    aboutCompany: '🏢 À propos',
    products: '🛍️ Produits',
    contact: '📞 Contact',
  },
  es: {
    trackOrder: '📦 Rastrear pedido',
    shipping: '🚚 Envío',
    returns: '↩️ Devoluciones',
    aboutCompany: '🏢 Sobre nosotros',
    products: '🛍️ Productos',
    contact: '📞 Contacto',
  },
  ar: {
    trackOrder: '📦 تتبع الطلب',
    shipping: '🚚 الشحن',
    returns: '↩️ المرتجعات',
    aboutCompany: '🏢 عن الشركة',
    products: '🛍️ المنتجات',
    contact: '📞 اتصل بنا',
  },
  it: {
    trackOrder: '📦 Traccia ordine',
    shipping: '🚚 Spedizione',
    returns: '↩️ Resi',
    aboutCompany: '🏢 Chi siamo',
    products: '🛍️ Prodotti',
    contact: '📞 Contatti',
  },
  pt: {
    trackOrder: '📦 Rastrear pedido',
    shipping: '🚚 Frete',
    returns: '↩️ Devoluções',
    aboutCompany: '🏢 Sobre nós',
    products: '🛍️ Produtos',
    contact: '📞 Contato',
  },
  ru: {
    trackOrder: '📦 Отследить заказ',
    shipping: '🚚 Доставка',
    returns: '↩️ Возврат',
    aboutCompany: '🏢 О компании',
    products: '🛍️ Товары',
    contact: '📞 Контакты',
  },
  zh: {
    trackOrder: '📦 订单追踪',
    shipping: '🚚 配送',
    returns: '↩️ 退货',
    aboutCompany: '🏢 关于我们',
    products: '🛍️ 产品',
    contact: '📞 联系我们',
  },
  fa: {
    trackOrder: '📦 پیگیری سفارش',
    shipping: '🚚 ارسال',
    returns: '↩️ مرجوعی',
    aboutCompany: '🏢 درباره ما',
    products: '🛍️ محصولات',
    contact: '📞 تماس',
  },
};

// Quick action messages in different languages
const QUICK_MESSAGES = {
  tr: {
    trackOrder: 'Siparişimi takip etmek istiyorum',
    shipping: 'Kargo hakkında bilgi almak istiyorum',
    returns: 'İade işlemi nasıl yapılır?',
    aboutCompany: 'Turkish E-Market hakkında bilgi verir misiniz?',
    products: 'Hangi ürünleri satıyorsunuz?',
    contact: 'Sizinle nasıl iletişime geçebilirim?',
  },
  en: {
    trackOrder: 'I want to track my order',
    shipping: 'I need shipping information',
    returns: 'How do I return an item?',
    aboutCompany: 'Can you tell me about Turkish E-Market?',
    products: 'What products do you sell?',
    contact: 'How can I contact you?',
  },
  de: {
    trackOrder: 'Ich möchte meine Bestellung verfolgen',
    shipping: 'Ich brauche Versandinformationen',
    returns: 'Wie kann ich einen Artikel zurückgeben?',
    aboutCompany: 'Können Sie mir über Turkish E-Market erzählen?',
    products: 'Welche Produkte verkaufen Sie?',
    contact: 'Wie kann ich Sie kontaktieren?',
  },
  fr: {
    trackOrder: 'Je veux suivre ma commande',
    shipping: 'Je veux des informations sur la livraison',
    returns: 'Comment retourner un article?',
    aboutCompany: 'Pouvez-vous me parler de Turkish E-Market?',
    products: 'Quels produits vendez-vous?',
    contact: 'Comment puis-je vous contacter?',
  },
  es: {
    trackOrder: 'Quiero rastrear mi pedido',
    shipping: 'Necesito información de envío',
    returns: '¿Cómo devuelvo un artículo?',
    aboutCompany: '¿Puede contarme sobre Turkish E-Market?',
    products: '¿Qué productos venden?',
    contact: '¿Cómo puedo contactarlos?',
  },
  ar: {
    trackOrder: 'أريد تتبع طلبي',
    shipping: 'أحتاج معلومات الشحن',
    returns: 'كيف يمكنني إرجاع منتج؟',
    aboutCompany: 'هل يمكنك إخباري عن Turkish E-Market؟',
    products: 'ما هي المنتجات التي تبيعونها؟',
    contact: 'كيف يمكنني التواصل معكم؟',
  },
  it: {
    trackOrder: 'Voglio tracciare il mio ordine',
    shipping: 'Ho bisogno di informazioni sulla spedizione',
    returns: 'Come posso restituire un articolo?',
    aboutCompany: 'Potete parlarmi di Turkish E-Market?',
    products: 'Quali prodotti vendete?',
    contact: 'Come posso contattarvi?',
  },
  pt: {
    trackOrder: 'Quero rastrear meu pedido',
    shipping: 'Preciso de informações sobre frete',
    returns: 'Como faço para devolver um item?',
    aboutCompany: 'Pode me falar sobre a Turkish E-Market?',
    products: 'Quais produtos vocês vendem?',
    contact: 'Como posso entrar em contato?',
  },
  ru: {
    trackOrder: 'Я хочу отследить свой заказ',
    shipping: 'Мне нужна информация о доставке',
    returns: 'Как вернуть товар?',
    aboutCompany: 'Расскажите о Turkish E-Market?',
    products: 'Какие товары вы продаете?',
    contact: 'Как с вами связаться?',
  },
  zh: {
    trackOrder: '我想追踪我的订单',
    shipping: '我需要配送信息',
    returns: '如何退货？',
    aboutCompany: '能介绍一下Turkish E-Market吗？',
    products: '你们卖什么产品？',
    contact: '如何联系你们？',
  },
  fa: {
    trackOrder: 'می‌خواهم سفارشم را پیگیری کنم',
    shipping: 'به اطلاعات ارسال نیاز دارم',
    returns: 'چگونه کالا را مرجوع کنم؟',
    aboutCompany: 'در مورد Turkish E-Market توضیح می‌دهید؟',
    products: 'چه محصولاتی می‌فروشید؟',
    contact: 'چگونه با شما تماس بگیرم؟',
  },
};

// UI Labels for different languages
const UI_LABELS = {
  tr: {
    customerSupport: 'Müşteri Desteği',
    aiPowered: 'AI destekli yardım',
    typing: 'Yazıyor...',
    needHelp: 'Yardım mı lazım?',
    clearChat: 'Sohbeti Temizle',
    customerRep: 'Müşteri Temsilcisi',
  },
  en: {
    customerSupport: 'Customer Support',
    aiPowered: 'AI-powered assistance',
    typing: 'Typing...',
    needHelp: 'Need help?',
    clearChat: 'Clear Chat',
    customerRep: 'Customer Representative',
  },
  de: {
    customerSupport: 'Kundensupport',
    aiPowered: 'KI-gestützte Hilfe',
    typing: 'Schreibt...',
    needHelp: 'Brauchen Sie Hilfe?',
    clearChat: 'Chat löschen',
    customerRep: 'Kundenberater',
  },
  fr: {
    customerSupport: 'Support Client',
    aiPowered: 'Assistance IA',
    typing: 'En train d\'écrire...',
    needHelp: 'Besoin d\'aide?',
    clearChat: 'Effacer le chat',
    customerRep: 'Représentant client',
  },
  es: {
    customerSupport: 'Soporte al Cliente',
    aiPowered: 'Asistencia con IA',
    typing: 'Escribiendo...',
    needHelp: '¿Necesitas ayuda?',
    clearChat: 'Limpiar chat',
    customerRep: 'Representante de cliente',
  },
  ar: {
    customerSupport: 'دعم العملاء',
    aiPowered: 'مساعدة مدعومة بالذكاء الاصطناعي',
    typing: 'جاري الكتابة...',
    needHelp: 'هل تحتاج مساعدة؟',
    clearChat: 'مسح المحادثة',
    customerRep: 'ممثل خدمة العملاء',
  },
  it: {
    customerSupport: 'Supporto Clienti',
    aiPowered: 'Assistenza AI',
    typing: 'Sta scrivendo...',
    needHelp: 'Hai bisogno di aiuto?',
    clearChat: 'Cancella chat',
    customerRep: 'Rappresentante clienti',
  },
  pt: {
    customerSupport: 'Suporte ao Cliente',
    aiPowered: 'Assistência com IA',
    typing: 'Digitando...',
    needHelp: 'Precisa de ajuda?',
    clearChat: 'Limpar chat',
    customerRep: 'Representante de atendimento',
  },
  ru: {
    customerSupport: 'Поддержка клиентов',
    aiPowered: 'ИИ-помощник',
    typing: 'Печатает...',
    needHelp: 'Нужна помощь?',
    clearChat: 'Очистить чат',
    customerRep: 'Представитель клиентской службы',
  },
  zh: {
    customerSupport: '客户支持',
    aiPowered: 'AI智能助手',
    typing: '正在输入...',
    needHelp: '需要帮助吗？',
    clearChat: '清除聊天',
    customerRep: '客户代表',
  },
  fa: {
    customerSupport: 'پشتیبانی مشتری',
    aiPowered: 'کمک هوش مصنوعی',
    typing: 'در حال نوشتن...',
    needHelp: 'به کمک نیاز دارید؟',
    clearChat: 'پاک کردن گفتگو',
    customerRep: 'نماینده پشتیبانی',
  },
};

const CustomerSupportChat = ({ embedded = false }) => {
  const [isOpen, setIsOpen] = useState(embedded);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [language, setLanguage] = useState('en');
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Detect language from browser or stored preference
  useEffect(() => {
    const storedLang = localStorage.getItem('support_chat_language');
    if (storedLang && LANGUAGES.some(l => l.code === storedLang)) {
      setLanguage(storedLang);
    } else {
      // Try to detect from browser
      const browserLang = navigator.language?.split('-')[0];
      if (LANGUAGES.some(l => l.code === browserLang)) {
        setLanguage(browserLang);
      }
    }
    
    // Load session from localStorage
    const storedSession = localStorage.getItem('support_chat_session');
    if (storedSession) {
      setSessionId(storedSession);
      loadChatHistory(storedSession);
    }
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Add greeting when chat is first opened (widget or embedded page)
  useEffect(() => {
    if ((isOpen || embedded) && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: GREETINGS[language] || GREETINGS.en,
        timestamp: new Date().toISOString()
      }]);
    }
  }, [isOpen, embedded, language, messages.length]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized]);

  useEffect(() => {
    if (!(isOpen || embedded) || !sessionId) return undefined;
    const t = setInterval(() => {
      loadChatHistory(sessionId);
    }, 5000);
    return () => clearInterval(t);
  }, [isOpen, embedded, sessionId]);

  const loadChatHistory = async (sid) => {
    try {
      const response = await fetch(`${API_URL}/api/support/history/${sid}`);
      if (response.ok) {
        const data = await response.json();
        if (data.messages && data.messages.length > 0) {
          setMessages(data.messages);
        }
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputMessage.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/support/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage.content,
          session_id: sessionId,
          language: language
        }),
      });

      const data = await response.json();
      
      // Save session ID
      if (data.session_id && !sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem('support_chat_session', data.session_id);
      }

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response,
        timestamp: data.timestamp
      }]);

      // Show unread badge if minimized
      if (isMinimized) {
        setUnreadCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString(),
        error: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLanguageChange = (langCode) => {
    setLanguage(langCode);
    localStorage.setItem('support_chat_language', langCode);
    setShowLanguageSelector(false);
    
    // Add language change message
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: GREETINGS[langCode] || GREETINGS.en,
      timestamp: new Date().toISOString()
    }]);
  };

  const clearChat = () => {
    setMessages([]);
    setSessionId(null);
    localStorage.removeItem('support_chat_session');
    
    // Add greeting in current language
    setMessages([{
      role: 'assistant',
      content: GREETINGS[language] || GREETINGS.en,
      timestamp: new Date().toISOString()
    }]);
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
    if (isMinimized) {
      setUnreadCount(0);
    }
  };

  const currentLang = LANGUAGES.find(l => l.code === language) || LANGUAGES[1];

  const windowClassName = embedded
    ? 'w-full h-[min(70vh,640px)] rounded-2xl shadow-lg border flex flex-col bg-white'
    : `fixed z-[9999] bg-white rounded-2xl shadow-2xl flex flex-col transition-all duration-300 ${
        isMinimized
          ? 'bottom-6 right-6 w-72 h-14'
          : 'bottom-6 right-6 w-[360px] sm:w-[400px] h-[500px] sm:h-[550px]'
      }`;

  return (
    <>
      {/* Chat Button */}
      {!embedded && !isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-[9999] bg-gradient-to-r from-orange-500 to-red-500 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105 group"
          data-testid="chat-widget-button"
        >
          <MessageCircle className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 bg-green-500 w-3 h-3 rounded-full animate-pulse" />
          
          {/* Tooltip */}
          <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-gray-900 text-white text-sm px-3 py-1.5 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
            {UI_LABELS[language]?.needHelp || UI_LABELS.en.needHelp}
          </span>
        </button>
      )}

      {/* Chat Window */}
      {(embedded || isOpen) && (
        <div
          className={windowClassName}
          data-testid={embedded ? 'support-page-chat' : 'chat-widget-window'}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-3 sm:p-4 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-full flex items-center justify-center">
                <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-sm sm:text-base">
                  {UI_LABELS[language]?.customerSupport || UI_LABELS.en.customerSupport}
                </h3>
                {!isMinimized && (
                  <p className="text-xs text-white/80">
                    {UI_LABELS[language]?.aiPowered || UI_LABELS.en.aiPowered}
                  </p>
                )}
              </div>
              {isMinimized && unreadCount > 0 && (
                <Badge className="bg-green-500 text-white ml-2">{unreadCount}</Badge>
              )}
            </div>
            <div className="flex items-center gap-1">
              {/* Language Selector */}
              {!isMinimized && (
                <div className="relative">
                  <button
                    onClick={() => setShowLanguageSelector(!showLanguageSelector)}
                    className="p-1.5 sm:p-2 hover:bg-white/20 rounded-lg transition-colors flex items-center gap-1"
                    title="Change language"
                  >
                    <span className="text-base sm:text-lg">{currentLang.flag}</span>
                    <Globe className="w-3 h-3 sm:w-4 sm:h-4" />
                  </button>
                  
                  {showLanguageSelector && (
                    <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-xl py-2 min-w-[140px] sm:min-w-[160px] z-10">
                      {LANGUAGES.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => handleLanguageChange(lang.code)}
                          className={`w-full px-3 sm:px-4 py-1.5 sm:py-2 text-left hover:bg-gray-100 flex items-center gap-2 text-sm ${
                            lang.code === language ? 'bg-orange-50 text-orange-600' : 'text-gray-700'
                          }`}
                        >
                          <span>{lang.flag}</span>
                          <span>{lang.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {!embedded && (
                <button
                  onClick={toggleMinimize}
                  className="p-1.5 sm:p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
                </button>
              )}
              {!embedded && (
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1.5 sm:p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Messages */}
          {!isMinimized && (
            <>
              <ScrollArea className="flex-1 p-3 sm:p-4">
                <div className="space-y-3 sm:space-y-4">
                  {messages.map((msg, index) => (
                    <div
                      key={index}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[85%] p-2.5 sm:p-3 rounded-2xl text-sm ${
                          msg.role === 'user'
                            ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-br-md'
                            : msg.role === 'admin'
                            ? 'bg-blue-500 text-white rounded-bl-md'
                            : msg.error
                            ? 'bg-red-100 text-red-700 rounded-bl-md'
                            : 'bg-gray-100 text-gray-800 rounded-bl-md'
                        }`}
                      >
                        {msg.role === 'admin' && (
                          <p className="text-[10px] text-blue-100 mb-1 font-medium">👤 {UI_LABELS[language]?.customerRep || UI_LABELS.en.customerRep}</p>
                        )}
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                        <p className={`text-[10px] mt-1 ${
                          msg.role === 'user' ? 'text-white/70' : msg.role === 'admin' ? 'text-blue-100' : 'text-gray-400'
                        }`}>
                          {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  ))}
                  
                  {isLoading && (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 p-3 rounded-2xl rounded-bl-md">
                        <div className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
                          <span className="text-sm text-gray-500">
                            {language === 'tr' ? 'Yazıyor...' : 'Typing...'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Input */}
              <form onSubmit={sendMessage} className="p-3 sm:p-4 border-t">
                <div className="flex gap-2">
                  <Input
                    ref={inputRef}
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    placeholder={PLACEHOLDERS[language] || PLACEHOLDERS.en}
                    className="flex-1 text-sm"
                    disabled={isLoading}
                    data-testid="chat-input"
                  />
                  <Button
                    type="submit"
                    size="icon"
                    disabled={!inputMessage.trim() || isLoading}
                    className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
                    data-testid="chat-send-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* Quick Actions */}
                <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-2 sm:mt-3">
                  <QuickButton onClick={() => setInputMessage(QUICK_MESSAGES[language]?.trackOrder || QUICK_MESSAGES.en.trackOrder)}>
                    {QUICK_ACTIONS[language]?.trackOrder || QUICK_ACTIONS.en.trackOrder}
                  </QuickButton>
                  <QuickButton onClick={() => setInputMessage(QUICK_MESSAGES[language]?.shipping || QUICK_MESSAGES.en.shipping)}>
                    {QUICK_ACTIONS[language]?.shipping || QUICK_ACTIONS.en.shipping}
                  </QuickButton>
                  <QuickButton onClick={() => setInputMessage(QUICK_MESSAGES[language]?.returns || QUICK_MESSAGES.en.returns)}>
                    {QUICK_ACTIONS[language]?.returns || QUICK_ACTIONS.en.returns}
                  </QuickButton>
                  <QuickButton onClick={() => setInputMessage(QUICK_MESSAGES[language]?.aboutCompany || QUICK_MESSAGES.en.aboutCompany)}>
                    {QUICK_ACTIONS[language]?.aboutCompany || QUICK_ACTIONS.en.aboutCompany}
                  </QuickButton>
                  <QuickButton onClick={() => setInputMessage(QUICK_MESSAGES[language]?.products || QUICK_MESSAGES.en.products)}>
                    {QUICK_ACTIONS[language]?.products || QUICK_ACTIONS.en.products}
                  </QuickButton>
                  <QuickButton onClick={() => setInputMessage(QUICK_MESSAGES[language]?.contact || QUICK_MESSAGES.en.contact)}>
                    {QUICK_ACTIONS[language]?.contact || QUICK_ACTIONS.en.contact}
                  </QuickButton>
                </div>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
};

// Quick action button component
const QuickButton = ({ children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded-full text-gray-600 transition-colors"
  >
    {children}
  </button>
);

export default CustomerSupportChat;
